/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

// Asks why a post is being reported. Closes with the reason, or nothing when cancelled.
@Component({
  selector: 'ut-report-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  template: `
    <form class="report-dialog" (ngSubmit)="submit()">
      <h2 class="report-dialog__title" mat-dialog-title>Report this post</h2>
      <p class="report-dialog__text">A moderator will review it. Your report is anonymous to the author.</p>
      <label class="report-dialog__label" for="reportReason">Why are you reporting this post?</label>
      <textarea id="reportReason" class="report-dialog__input" name="reason" rows="4" cdkFocusInitial [(ngModel)]="reason"></textarea>
      <div class="report-dialog__actions">
        <button type="button" class="ut-btn ut-btn--secondary" mat-dialog-close>Cancel</button>
        <button type="submit" class="ut-btn" [disabled]="!reason.trim()">Report</button>
      </div>
    </form>
  `,
  styles: [`
    .report-dialog {
      width: min(48rem, 90vw);
      padding: var(--spacing-3xl);
      font-family: var(--font-body);
    }

    .report-dialog__title {
      margin: 0;
      padding: 0;
      color: var(--text-primary);
      font-size: var(--text-lg);
      line-height: var(--text-lg-lh);
      font-weight: 600;
    }

    .report-dialog__title::before {
      display: none;
    }

    .report-dialog__text {
      margin: var(--spacing-xs) 0 var(--spacing-2xl);
      color: var(--text-tertiary);
      font-size: var(--text-sm);
      line-height: var(--text-sm-lh);
    }

    .report-dialog__label {
      display: block;
      margin-bottom: var(--spacing-sm);
      color: var(--text-secondary);
      font-size: var(--text-sm);
      font-weight: 500;
    }

    .report-dialog__input {
      width: 100%;
      padding: var(--spacing-md) var(--spacing-lg);
      border: 1px solid var(--border-primary);
      border-radius: var(--radius-md);
      box-shadow: var(--shadow-xs);
      color: var(--text-primary);
      font-size: var(--text-md);
      line-height: var(--text-md-lh);
      resize: vertical;
    }

    .report-dialog__input:focus {
      outline: none;
      border-color: var(--brand-600);
      box-shadow: var(--shadow-xs), 0 0 0 4px var(--brand-50);
    }

    .report-dialog__actions {
      display: flex;
      gap: var(--spacing-lg);
      margin-top: var(--spacing-4xl);
    }

    .report-dialog__actions .ut-btn {
      flex: 1;
    }
  `],
})
export class ReportDialogComponent {
  reason = '';

  constructor(private dialogRef: MatDialogRef<ReportDialogComponent, string>) {}

  submit(): void {
    if (this.reason.trim()) this.dialogRef.close(this.reason.trim());
  }
}
